
import { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import ReactMarkdown from 'react-markdown';
import { 
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from '@/components/ui/pagination';
import { Youtube, Book, PenLine } from 'lucide-react';
import type { NewsItem } from '@/components/news/NewsPreview';
import { supabase } from "@/integrations/supabase/client";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Link } from "react-router-dom";
import { Post } from '@/types/board';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

const News = () => {
  // Authentication and toast
  const { user } = useAuth();
  const { toast } = useToast();
  
  // YouTube news state
  const [currentPage, setCurrentPage] = useState(1);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 8;

  // Local news state
  const [localNews, setLocalNews] = useState<Post[]>([]);
  const [isLoadingLocal, setIsLoadingLocal] = useState(true);
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [localTotalCount, setLocalTotalCount] = useState(0);
  const localItemsPerPage = 10;

  // Fetch local news with pagination
  useEffect(() => {
    const fetchLocalNews = async () => {
      setIsLoadingLocal(true);
      try {
        // Get total count
        const { count, error: countError } = await supabase
          .from('board_posts')
          .select('*', { count: 'exact', head: true })
          .eq('category', '로컬뉴스');
        
        if (countError) {
          console.error('Error counting local news:', countError);
        } else {
          setLocalTotalCount(count || 0);
        }
        
        const from = (localCurrentPage - 1) * localItemsPerPage;
        const to = from + localItemsPerPage - 1;
        
        const { data, error } = await supabase
          .from('board_posts')
          .select('*')
          .eq('category', '로컬뉴스')
          .order('created_at', { ascending: false })
          .range(from, to);
        
        if (error) {
          console.error('Error fetching local news:', error);
          return;
        }
        
        setLocalNews(data as Post[]);
      } catch (error) {
        console.error('Failed to fetch local news:', error);
      } finally {
        setIsLoadingLocal(false);
      }
    };
    
    fetchLocalNews();
  }, [localCurrentPage]);

  // Fetch YouTube news
  useEffect(() => {
    const fetchNews = async () => {
      setIsLoading(true);
      try {
        const { count, error: countError } = await supabase
          .from('news')
          .select('*', { count: 'exact', head: true });
        
        if (countError) {
          console.error('Error counting news:', countError);
        } else {
          setTotalCount(count || 0);
        }
        
        const from = (currentPage - 1) * itemsPerPage;
        const to = from + itemsPerPage - 1;
        
        const { data, error } = await supabase
          .from('news')
          .select('id, title, videoid, publishedat')
          .order('publishedat', { ascending: false })
          .range(from, to);
        
        if (error) {
          console.error('Error fetching news:', error);
          return;
        }
        
        const transformedData: NewsItem[] = data.map(item => ({
          id: item.id.toString(),
          title: item.title,
          thumbnailUrl: `https://i.ytimg.com/vi/${item.videoid}/maxresdefault.jpg`,
          videoUrl: `https://www.youtube.com/watch?v=${item.videoid}`
        }));
        
        setNewsItems(transformedData);
      } catch (error) {
        console.error('Failed to fetch news:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchNews();
  }, [currentPage]);
  
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const localTotalPages = Math.ceil(localTotalCount / localItemsPerPage);

  // Updated function to format date as mm.dd
  const formatDate = (date: string) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    
    return `${month}.${day}`;
  };

  // Helper function to get author name safely
  const getAuthorName = (post: Post) => {
    return post.author_name || 'Anonymous';
  };
  
  const handleWriteClick = () => {
    if (!user) {
      toast({
        title: "로그인이 필요합니다",
        description: "로컬뉴스를 작성하려면 로그인이 필요합니다.",
        variant: "destructive",
      });
      return;
    }
  };

  // Check if the current user has the specific email
  const isAuthorizedUser = user?.email === "ilwoonge@gmail.com";
  
  return (
    <Layout>
      <div className="container py-6">
        {/* Local News Section */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Book className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-bold">로컬 뉴스</h1>
              </div>
              {isAuthorizedUser && (
                <Button 
                  asChild
                  className="gap-1"
                >
                  <Link to="/board/write?category=로컬뉴스">
                    <PenLine className="h-4 w-4" />
                    작성하기
                  </Link>
                </Button>
              )}
            </div>
            
            {isLoadingLocal ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-8 bg-muted rounded w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>제목</TableHead>
                    <TableHead className="w-[100px] whitespace-nowrap">작성일</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {localNews.length > 0 ? (
                    localNews.map((post) => (
                      <TableRow key={post.id}>
                        <TableCell>
                          <Link 
                            to={`/news/${post.id}`} 
                            className="hover:text-primary hover:underline"
                          >
                            {post.title}
                          </Link>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{formatDate(post.created_at || post.createdAt || '')}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center py-8 text-muted-foreground">
                        로컬뉴스가 없습니다.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
            
            {localTotalPages > 1 && (
              <Pagination className="mt-6">
                <PaginationContent>
                  {localCurrentPage > 1 && (
                    <PaginationItem>
                      <PaginationPrevious 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          setLocalCurrentPage(prev => Math.max(prev - 1, 1));
                        }} 
                      />
                    </PaginationItem>
                  )}
                  
                  {Array.from({ length: localTotalPages }).map((_, index) => (
                    <PaginationItem key={index}>
                      <PaginationLink 
                        href="#" 
                        isActive={localCurrentPage === index + 1}
                        onClick={(e) => {
                          e.preventDefault();
                          setLocalCurrentPage(index + 1);
                        }}
                      >
                        {index + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  
                  {localCurrentPage < localTotalPages && (
                    <PaginationItem>
                      <PaginationNext 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          setLocalCurrentPage(prev => Math.min(prev + 1, localTotalPages));
                        }} 
                      />
                    </PaginationItem>
                  )}
                </PaginationContent>
              </Pagination>
            )}
          </CardContent>
        </Card>

        {/* YouTube News Section */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <Youtube className="h-6 w-6 text-red-600" />
              <h1 className="text-2xl font-bold">유투브 뉴스</h1>
            </div>
            
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="space-y-2">
                    <div className="relative pb-[56.25%] rounded-md overflow-hidden bg-muted">
                      <div className="absolute inset-0 w-full h-full" />
                    </div>
                    <div className="h-4 w-full bg-muted rounded" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                {newsItems.map((item) => (
                  <a 
                    key={item.id}
                    href={item.videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group hover:opacity-90 transition-opacity"
                  >
                    <div className="relative pb-[56.25%] rounded-md overflow-hidden mb-2 border border-border/50 bg-secondary">
                      <img 
                        src={item.thumbnailUrl} 
                        alt={item.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = "/placeholder.svg";
                        }}
                      />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-primary/80 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white" className="ml-1">
                            <polygon points="5 3 19 12 5 21 5 3"></polygon>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                      <div className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">
                        <ReactMarkdown>{item.title}</ReactMarkdown>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
            
            {totalPages > 1 && (
              <Pagination className="mt-6">
                <PaginationContent>
                  {currentPage > 1 && (
                    <PaginationItem>
                      <PaginationPrevious 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(prev => Math.max(prev - 1, 1));
                        }} 
                      />
                    </PaginationItem>
                  )}
                  
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <PaginationItem key={index}>
                      <PaginationLink 
                        href="#" 
                        isActive={currentPage === index + 1}
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(index + 1);
                        }}
                      >
                        {index + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  
                  {currentPage < totalPages && (
                    <PaginationItem>
                      <PaginationNext 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(prev => Math.min(prev + 1, totalPages));
                        }} 
                      />
                    </PaginationItem>
                  )}
                </PaginationContent>
              </Pagination>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default News;
